"use client";

import { useEffect, useRef, useState } from "react";

import { CheckIcon, CopyIcon } from "../Icons";
import { TooltipWrapper } from "../Tooltip";

const COPIED_RESET_MS = 3000;

export type CopyButtonProps = {
  text: string;
  copiedLabel: string;
  copyLabel?: string;
  onCopy?: () => void;
  isMobile?: boolean;
  id?: string;
  className?: string;
  /**
   * Whether a "Copied" tooltip pops open on success. Defaults to `true`, which
   * every existing consumer was built against.
   *
   * Pass `false` where the icon swap is the whole confirmation — the DS copy
   * button ("swaps to a green check for 3s after a successful copy", canvas
   * `CopyLinkButton`) has no tooltip, so a tooltip on top reads as double
   * feedback. The `aria-label` still flips to `copiedLabel` either way.
   */
  showCopiedTooltip?: boolean;
  "data-testid"?: string;
};

export const CopyButton = ({
  text,
  copiedLabel,
  copyLabel = "Copy",
  onCopy,
  isMobile = false,
  id,
  className,
  showCopiedTooltip = true,
  "data-testid": dataTestId = "copy-button",
}: CopyButtonProps) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      onCopy?.();
      setCopied(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), COPIED_RESET_MS);
    } catch (err) {
      console.error("CopyButton: failed to copy text", err);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const button = isMobile ? (
    <button
      type="button"
      aria-label={copied ? copiedLabel : copyLabel}
      data-testid={copied ? `${dataTestId}-copied` : dataTestId}
      onClick={handleCopy}
      className={`flex h-8 w-8 items-center justify-center rounded-lg border border-stroke-primary text-text-primary ${className ?? ""}`}
    >
      {copied ? (
        <span className="text-text-positive-default">
          <CheckIcon />
        </span>
      ) : (
        <CopyIcon />
      )}
    </button>
  ) : (
    <button
      type="button"
      aria-label={copied ? copiedLabel : copyLabel}
      data-testid={copied ? `${dataTestId}-copied` : dataTestId}
      onClick={handleCopy}
      className={`flex h-6 w-6 items-center justify-center rounded-lg hover:bg-overlay-opacity-state-hover ${className ?? ""}`}
    >
      {copied ? (
        <span className="text-text-positive-default">
          <CheckIcon />
        </span>
      ) : (
        <span className="text-text-brand-default">
          <CopyIcon />
        </span>
      )}
    </button>
  );

  if (!showCopiedTooltip) return button;

  return (
    <TooltipWrapper
      id={id}
      isMobile={isMobile}
      text={copiedLabel}
      tooltipPlacement="top"
      tooltipTextAlign="text-center"
      color="blue"
      tooltipContainerClassName="!w-[unset]"
      isOpen={copied}
      openEvents={{ click: true, mouseover: false }}
    >
      {button}
    </TooltipWrapper>
  );
};
